import { supabase } from '../lib/supabase';

export interface Organization {
  id: string;
  name: string;
  abn: string;
  dealer_license: string;
  motor_dealer_license?: string; // Alternative field name
  address: string;
  phone: string;
  email: string;
  state?: string;
  // New split address fields
  address_line1?: string;
  address_line2?: string;
  suburb?: string;
  postcode?: string;
  // Bank account details
  bank_name?: string;
  bank_account_name?: string;
  bsb?: string;
  account_number?: string;
  created_at?: string;
  updated_at?: string;
}

export interface OrganizationValidationError {
  field: string;
  message: string;
  severity: 'error' | 'warning';
}

export class OrganizationService {
  private static cachedOrg: Organization | null = null;
  private static cacheTimestamp: number = 0;
  private static cachedOrgId: string | null = null;
  private static orgIdCacheTimestamp: number = 0;
  private static CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  /**
   * Get just the organization ID for the current user
   * Uses separate cache for performance - much faster than getOrganization()
   * @param force - Force refresh from database (bypass cache)
   * @returns Organization ID string
   */
  static async getOrganizationId(force = false): Promise<string> {
    // Use cache if valid and not forced
    const now = Date.now();
    if (!force && this.cachedOrgId && (now - this.orgIdCacheTimestamp) < this.CACHE_DURATION) {
      console.log('[OrganizationService] Using cached organization_id');
      return this.cachedOrgId;
    }

    // Get current user
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) {
      throw new Error('Not authenticated. Please log in.');
    }

    // Get user's organization_id
    console.log('[OrganizationService] Fetching organization_id from database');
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('organization_id')
      .eq('id', user.id)
      .single();

    if (userError || !userData?.organization_id) {
      throw new Error('User not associated with an organization. Please contact support.');
    }

    // Cache the result
    this.cachedOrgId = userData.organization_id;
    this.orgIdCacheTimestamp = now;

    return this.cachedOrgId;
  }

  /**
   * Get organization data for the current user
   * @param force - Force refresh from database (bypass cache)
   * @returns Organization data or throws error
   */
  static async getOrganization(force = false): Promise<Organization> {
    // Use cache if valid and not forced
    const now = Date.now();
    if (!force && this.cachedOrg && (now - this.cacheTimestamp) < this.CACHE_DURATION) {
      return this.cachedOrg;
    }

    // Get current user
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) {
      throw new Error('Not authenticated. Please log in.');
    }

    // Get user's organization_id
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('organization_id')
      .eq('id', user.id)
      .single();

    if (userError || !userData?.organization_id) {
      throw new Error('User not associated with an organization. Please contact support.');
    }

    // Get organization data
    const { data: org, error: orgError } = await supabase
      .from('organizations')
      .select('*')
      .eq('id', userData.organization_id)
      .single();

    if (orgError || !org) {
      throw new Error('Organization not found. Please contact support.');
    }

    // Cache the result
    this.cachedOrg = org as Organization;
    this.cacheTimestamp = now;

    return this.cachedOrg;
  }

  /**
   * Validate organization data for document generation
   * @param org - Organization data to validate
   * @returns Array of validation errors (empty if valid)
   */
  static validateForDocumentGeneration(org: Organization): OrganizationValidationError[] {
    const errors: OrganizationValidationError[] = [];

    // Critical fields - MUST be present and valid
    if (!org.name || org.name === 'DealerOS' || org.name.trim().length === 0) {
      errors.push({
        field: 'name',
        message: 'Business name is required. Please set it in Settings → Organization.',
        severity: 'error'
      });
    }

    if (!org.dealer_license || org.dealer_license === 'MD12345' || org.dealer_license.length < 5) {
      errors.push({
        field: 'dealer_license',
        message: 'Motor Dealer License is required. Please set your real license number in Settings.',
        severity: 'error'
      });
    }

    if (!org.abn || org.abn === 'ABN 12 345 678 901' || org.abn === '12 345 678 901') {
      errors.push({
        field: 'abn',
        message: 'ABN is required for tax invoices. Please set your real ABN in Settings.',
        severity: 'error'
      });
    }

    // Validate ABN format (11 digits)
    if (org.abn) {
      const abnDigits = org.abn.replace(/\s/g, '');
      if (abnDigits.length !== 11 || !/^\d+$/.test(abnDigits)) {
        errors.push({
          field: 'abn',
          message: 'ABN must be 11 digits. Current format is invalid.',
          severity: 'error'
        });
      }
    }

    // Check if new address fields exist and are filled (address_line2 is optional)
    const hasNewAddressFields = org.address_line1 && org.suburb && org.state && org.postcode;

    // Validate address - accept either old format or new split fields
    if (!hasNewAddressFields && (!org.address || org.address === '42 Parramatta Road, Granville NSW 2142')) {
      errors.push({
        field: 'address',
        message: 'Business address is required. Please set your real address in Settings.',
        severity: 'error'
      });
    }

    if (!org.phone || org.phone === '02 9637 8888') {
      errors.push({
        field: 'phone',
        message: 'Phone number is required. Please set your real phone number in Settings.',
        severity: 'error'
      });
    }

    if (!org.email || org.email === 'info@hotspotautos.com.au' || org.email === 'sales@dealeros.com.au') {
      errors.push({
        field: 'email',
        message: 'Email address is required. Please set your real email in Settings.',
        severity: 'warning'
      });
    }

    return errors;
  }

  /**
   * Get organization data and validate for document generation
   * Throws error if validation fails
   * @returns Validated organization data
   */
  static async getValidatedOrganization(): Promise<Organization> {
    const org = await this.getOrganization();
    const errors = this.validateForDocumentGeneration(org);

    const criticalErrors = errors.filter(e => e.severity === 'error');

    if (criticalErrors.length > 0) {
      const errorMessages = criticalErrors.map(e => `• ${e.message}`).join('\n');
      throw new Error(
        `Cannot generate documents. Please complete your organization settings first:\n\n${errorMessages}\n\nGo to Settings → Organization to fix these issues.`
      );
    }

    // Log warnings but don't throw
    const warnings = errors.filter(e => e.severity === 'warning');
    if (warnings.length > 0) {
      console.warn('Organization validation warnings:', warnings);
    }

    return org;
  }

  /**
   * Check if organization is properly configured for document generation
   * @returns { valid: boolean, errors: ValidationError[] }
   */
  static async checkOrganizationSetup(): Promise<{
    valid: boolean;
    errors: OrganizationValidationError[];
    warnings: OrganizationValidationError[];
  }> {
    try {
      const org = await this.getOrganization();
      const errors = this.validateForDocumentGeneration(org);

      const criticalErrors = errors.filter(e => e.severity === 'error');
      const warnings = errors.filter(e => e.severity === 'warning');

      return {
        valid: criticalErrors.length === 0,
        errors: criticalErrors,
        warnings
      };
    } catch (error) {
      return {
        valid: false,
        errors: [{
          field: 'general',
          message: error.message || 'Failed to load organization data',
          severity: 'error'
        }],
        warnings: []
      };
    }
  }

  /**
   * Clear cached organization data
   * Call this after updating organization settings
   */
  static clearCache(): void {
    this.cachedOrg = null;
    this.cacheTimestamp = 0;
  }

  /**
   * Format organization data for PDF generation
   * Returns object ready for VehicleCardGenerator
   */
  static formatForPDF(org: Organization): {
    name: string;
    abn: string;
    dealer_license: string;
    address: string;
    phone: string;
    email: string;
  } {
    return {
      name: org.name,
      abn: org.abn,
      dealer_license: org.dealer_license,
      address: org.address,
      phone: org.phone,
      email: org.email
    };
  }
}

export default OrganizationService;
